import { motion } from 'framer-motion';
import { microInteractions } from '../../utils/animations';

/**
 * Empty State Component
 * Creative, illustrated empty states for no data scenarios
 */
export default function EmptyState({ 
  icon, 
  title, 
  description, 
  action,
  className = '' 
}) {
  return (
    <motion.div
      {...microInteractions.fadeIn}
      className={`text-center py-16 px-4 ${className}`}
    >
      {/* Icon/Illustration */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
        className="mb-6"
      >
        {icon}
      </motion.div>

      {/* Title */}
      <motion.h3
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-heading-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"
      >
        {title}
      </motion.h3>

      {/* Description */}
      <motion.p
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-body text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto"
      >
        {description}
      </motion.p>

      {/* Action */}
      {action && (
        <motion.div
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {action}
        </motion.div>
      )}
    </motion.div>
  );
}

export function NoDesignsEmpty({ onAction }) {
  return (
    <EmptyState
      icon={
        <svg className="w-32 h-32 mx-auto text-gray-300 dark:text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      }
      title="No designs yet"
      description="Create designs in Canva and they'll sync here automatically — ready to deliver to your media stations with one click, or hands-free on your auto-sync schedule."
      action={onAction}
    />
  );
}

export function NoAgentsEmpty({ onAction }) {
  return (
    <EmptyState
      icon={
        <svg className="w-32 h-32 mx-auto text-gray-300 dark:text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      }
      title="No Media Stations linked yet"
      description="Link a Media Station — the companion that delivers prepared designs to your presentation software."
      action={onAction}
    />
  );
}

export function NoSearchResultsEmpty({ query, onAction }) {
  return (
    <EmptyState
      icon={
        <svg className="w-32 h-32 mx-auto text-gray-300 dark:text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      }
      title="No hymns match your search"
      description={`No designs found for "${query}". Try a different title, or clear the search.`}
      action={onAction}
    />
  );
}

export function CanvaNotConnectedEmpty({ onAction }) {
  return (
    <EmptyState
      icon={
        <svg className="w-32 h-32 mx-auto text-primary-400 dark:text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      }
      title="Connect Your Canva Account"
      description="Connect Canva so TurboSync Pro can continuously sync your designs and prepare them for live presentation use."
      action={onAction}
    />
  );
}
